/**
 * Input gathering: WASD/arrows relative to the camera, click-to-move target,
 * dodge on Space (seven-button ceiling in mind — Phase 3 needs movement +
 * dodge only, AD-021).
 */

import type { MovementInput } from '@great-cloud/shared/client';

import { isGameKey } from './keyTarget.js';
import { runTeardown } from './teardown.js';
import { stickChannel, type StickChannel } from './touch/stickChannel.js';

const CLICK_ARRIVE_RADIUS = 0.25;

/**
 * The window event the HUD's basic-attack button raises, so a click takes the SAME
 * path as the F key (soft target → face / close / swing, combat.md §Control model)
 * instead of a bare `attackIntent` that only faces the last walk direction.
 */
export const BASIC_ATTACK_EVENT = 'tgc:basic-attack';

/** Raise a basic-attack press from outside the game loop (the HUD button; a future touch tap). */
export function requestBasicAttack(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(BASIC_ATTACK_EVENT));
}

/**
 * The window event the touch overlay's Dodge button raises (mobile-layout.md §7.2), so it
 * queues the SAME dodge as Space — dashing along the stick's direction when it is held.
 */
export const DODGE_EVENT = 'tgc:dodge';

/** Raise a dodge press from outside the game loop (the touch overlay's Dodge button). */
export function requestDodge(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(DODGE_EVENT));
}

/**
 * Map a stick-space direction (`ix` right, `iz` forward) to a world-space unit move vector
 * relative to the camera. The ONE mapping for the keys and the on-screen stick (T3), so the
 * stick drives exactly the movement WASD does:
 * forward (W) = the direction the camera looks, (sin yaw, cos yaw);
 * right (D)   = screen-right of that view, (-cos yaw, sin yaw) in our Y-up right-handed
 * world. (Playtest 2026-06-11: the original version rotated with the wrong handedness,
 * mirroring A/D.) Returns zeros for a zero input.
 */
export function cameraRelativeMove(
  ix: number,
  iz: number,
  cameraYaw: number,
): { moveX: number; moveZ: number } {
  if (ix === 0 && iz === 0) return { moveX: 0, moveZ: 0 };
  const sin = Math.sin(cameraYaw);
  const cos = Math.cos(cameraYaw);
  const x = iz * sin - ix * cos;
  const z = iz * cos + ix * sin;
  const mag = Math.hypot(x, z);
  return { moveX: x / mag, moveZ: z / mag };
}

const MOVE_KEYS = ['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'];

export class InputState {
  private keys = new Set<string>();
  private dodgeQueued = false;
  private attackQueued = false;
  private moveTarget: { x: number; z: number } | null = null;
  private lastFacing = 0;
  /** A facing to hold while not moving (an attack press facing its soft target). */
  private facingOverride: number | null = null;
  private el: HTMLElement | null = null;

  /** `stick` — the on-screen stick's direction (T3); injectable for tests. */
  constructor(private readonly stick: StickChannel = stickChannel) {}

  private onAttackRequest = (): void => {
    this.attackQueued = true;
  };

  private onDodgeRequest = (): void => {
    this.dodgeQueued = true;
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    // Chat / mail / a panel field owns its keystrokes, and Ctrl+W is the browser's.
    if (!isGameKey(e, typeof document === 'undefined' ? undefined : document.activeElement)) return;
    const k = e.key.toLowerCase();
    if (k === ' ') {
      this.dodgeQueued = true;
      e.preventDefault();
      return;
    }
    if (k === 'f') {
      this.attackQueued = true;
      e.preventDefault();
      return;
    }
    this.keys.add(k);
    if (MOVE_KEYS.includes(k)) {
      this.moveTarget = null; // keyboard overrides click-to-move
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.key.toLowerCase());
  };

  private onBlur = (): void => {
    this.keys.clear();
    this.stick.clear();
  };

  attach(el: HTMLElement): void {
    this.el = el;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener(BASIC_ATTACK_EVENT, this.onAttackRequest);
    window.addEventListener(DODGE_EVENT, this.onDodgeRequest);
  }

  /** Guarded per step (H-258), on the same terms as `OrbitCamera.dispose()`. */
  dispose(): void {
    runTeardown([
      () => window.removeEventListener('keydown', this.onKeyDown),
      () => window.removeEventListener('keyup', this.onKeyUp),
      () => window.removeEventListener('blur', this.onBlur),
      () => window.removeEventListener(BASIC_ATTACK_EVENT, this.onAttackRequest),
      () => window.removeEventListener(DODGE_EVENT, this.onDodgeRequest),
      () => this.stick.clear(), // a remounted game never inherits a held stick
      () => {
        this.el = null;
      },
    ]);
  }

  setMoveTarget(x: number, z: number): void {
    this.moveTarget = { x, z };
  }

  clearMoveTarget(): void {
    this.moveTarget = null;
  }

  get hasMoveTarget(): boolean {
    return this.moveTarget !== null;
  }

  /** A movement key is held — the player is steering, so an attack walk-to yields. */
  get keyboardMoving(): boolean {
    return MOVE_KEYS.some((k) => this.keys.has(k));
  }

  /**
   * The player is steering — a movement key held OR the on-screen stick past its dead zone
   * (mobile-layout.md §7.1). Steering takes the body back: it abandons a walk-to and drops
   * the explicit target (combat.md §Control model, AD-817), the same for both.
   */
  get steering(): boolean {
    return this.keyboardMoving || this.stick.active;
  }

  /**
   * Hold `facing` on the next samples that carry no movement (combat.md §Control
   * model: an attack press faces its soft target). A facing-only input never
   * cancels a gather (AD-665); any movement replaces it.
   */
  setFacing(facing: number): void {
    this.facingOverride = facing;
  }

  /** Queue a basic attack as if F were pressed (also raised by `requestBasicAttack`). */
  queueAttack(): void {
    this.attackQueued = true;
  }

  /** Consume a queued basic attack (P4.6); true at most once per key press. */
  consumeAttack(): boolean {
    const a = this.attackQueued;
    this.attackQueued = false;
    return a;
  }

  /** Sample one input for the next simulation step. Consumes a queued dodge. */
  sample(cameraYaw: number, playerX: number, playerZ: number): MovementInput {
    let ix = 0;
    let iz = 0;
    if (this.keys.has('w') || this.keys.has('arrowup')) iz += 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) iz -= 1;
    if (this.keys.has('a') || this.keys.has('arrowleft')) ix -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) ix += 1;

    // Keys win; else the on-screen stick (T3) — both through the one camera-relative mapping.
    if (ix === 0 && iz === 0 && this.stick.active) {
      const d = this.stick.direction;
      ix = d.ix;
      iz = d.iz;
      this.moveTarget = null; // the stick overrides click-to-move, as a key does
    }

    let moveX = 0;
    let moveZ = 0;
    if (ix !== 0 || iz !== 0) {
      ({ moveX, moveZ } = cameraRelativeMove(ix, iz, cameraYaw));
    } else if (this.moveTarget) {
      const dx = this.moveTarget.x - playerX;
      const dz = this.moveTarget.z - playerZ;
      const dist = Math.hypot(dx, dz);
      if (dist <= CLICK_ARRIVE_RADIUS) {
        this.moveTarget = null;
      } else {
        moveX = dx / dist;
        moveZ = dz / dist;
      }
    }

    if (moveX !== 0 || moveZ !== 0) {
      this.lastFacing = Math.atan2(moveX, moveZ);
      this.facingOverride = null;
    } else if (this.facingOverride !== null) {
      this.lastFacing = this.facingOverride;
      this.facingOverride = null;
    }

    const dodge = this.dodgeQueued;
    this.dodgeQueued = false;
    return { moveX, moveZ, facing: this.lastFacing, dodge };
  }
}
